<template>
  <div>
    <alert ref="alert" />
    <manage-cashflows-modal ref="manageCashflowsModal"
      :cashflows="userCollCashflows"
      @deleteCashflow="onCashflowDeleted"
      @loadCashflow="loadFromExcel" />

    <div v-if="showSpinner" class="loader-wrapper">
      <div class="loader"></div>
    </div>

    <b-row class="mt-2 mb-2">
      <b-col cols="3">
        <b-form-group class="text-left">
          <template slot="label">
            <h3>Cashflows</h3>
          </template>
          <b-form-select id="cashflow_select" v-model="selectedCashflow" :options="userCollCashflows" @change="changeSelectedCashflow" :disabled="userCollCashflows.length == 0" />
        </b-form-group>
      </b-col>
      <b-col cols="9" class="text-right">
        <b-btn variant="info" class="text-nowrap mr-1" @click="showModal()">Manage cashflows</b-btn>
        <b-btn variant="info" class="text-nowrap mr-1" @click="save">Save</b-btn>
      </b-col>
    </b-row>
    <user-coll-cashflow v-if="userCollCashflows.length && selectedCashflow.CollateralCashflow != undefined" :collateralCashflow="selectedCashflow.CollateralCashflow" />
  </div>
</template>

<script>
import service from '../../services/HttpRequestService';
import UserCollCashflow from './UserCollateralCashflow';
import { UserCollateralCashflowClass, CollateralCashflow } from './UserCollateralCashflowClass';
import formatter from '../../utilities/Formatter';
import ManageCashflowsModal from './ManageCashflowsModal';
import Alert from '../Alert';
import { TRACE_ERROR } from '../../constants';

export default {
  data() {
    return {
      userCollCashflows: [],
      selectedCashflow: '',
      unsavedChanges: false,
      showSpinner: true
    };
  },
  async created() {
    await this.loadCashflows();
  },
  methods: {
    async loadCashflows() {
      try {
        let res = await service.getUserCollateralCashflows();
        for (let userCashflow of res.data) {
          for (let key of Object.keys(userCashflow.CollateralCashflow)) {
            for (let i = 0; i < userCashflow.CollateralCashflow[key].length; i++) {
              userCashflow.CollateralCashflow[key][i] = formatter.formatDollarValue(
                userCashflow.CollateralCashflow[key][i]
              );
            }
          }
          let text =
            userCashflow.DealName +
            ' / ' +
            userCashflow.CollateralMonth +
            ' / ' +
            userCashflow.ScenarioName;
          if (userCashflow.Vendor) {
            text += ' / ' + userCashflow.Vendor;
          }
          this.userCollCashflows.push({
            text: text,
            value: new UserCollateralCashflowClass(
              userCashflow.DealName,
              userCashflow.CollateralMonth,
              userCashflow.ScenarioName,
              userCashflow.Vendor,
              userCashflow.CollateralCashflow
            )
          });
          this.changeSelectedCashflow(this.userCollCashflows[0].value);
        }
      } catch (err) {
        await service.log(TRACE_ERROR, err.response.data, err.stack);
        this.$refs.alert.showAlert('danger', err.response.data);
      } finally {
        this.showSpinner = false;
      }
    },
    changeSelectedCashflow(cashflow) {
      this.selectedCashflow = cashflow;
    },
    onCashflowDeleted(updated) {
      this.userCollCashflows = updated;
      if (this.userCollCashflows.length) {
        this.changeSelectedCashflow(this.userCollCashflows[0].value);
      }
      this.casfhlowsChanged();
    },
    async loadFromExcel(filename, data) {
      let vendor, dealName, collateralMonth, scenarioName, rest;
      [dealName, collateralMonth, scenarioName, vendor, ...rest] = filename.split('.');
      let collateralCashflow = new CollateralCashflow([], [], [], [], [], [], [], [], [], []);

      let collateralCashflowsSet = new Set(this.userCollCashflows.map(c => c.text));
      for (let i = 1; i < data.length; i++) {
        collateralCashflow.Balance.push(formatter.formatDollarValue(data[i][1]));
        collateralCashflow.Interest.push(formatter.formatDollarValue(data[i][2]));
        collateralCashflow.PassThru.push(formatter.formatDollarValue(data[i][3]));
        collateralCashflow.ScheduledPrincipal.push(formatter.formatDollarValue(data[i][4]));
        collateralCashflow.PrepaidPrincipal.push(formatter.formatDollarValue(data[i][5]));
        collateralCashflow.Defaults.push(formatter.formatDollarValue(data[i][6]));
        collateralCashflow.Losses.push(formatter.formatDollarValue(data[i][7]));
        collateralCashflow.Recoveries.push(formatter.formatDollarValue(data[i][8]));
        collateralCashflow.Delinquency.push(formatter.formatDollarValue(data[i][9]));
        collateralCashflow.Libor.push(formatter.formatDollarValue(data[i][10]));
      }

      let text = dealName + ' / ' + collateralMonth + ' / ' + scenarioName;
      if (vendor) {
        text += ' / ' + vendor;
      }

      let cashflowObj = {
        text: text,
        value: new UserCollateralCashflowClass(
          dealName,
          collateralMonth,
          scenarioName,
          vendor,
          collateralCashflow
        )
      };

      if (!collateralCashflowsSet.has(cashflowObj.text)) {
        this.userCollCashflows.push(cashflowObj);
        this.selectedCashflow = this.userCollCashflows[this.userCollCashflows.length - 1].value;
        this.casfhlowsChanged();
      } else {
        this.$refs.alert.showAlert(
          'danger',
          'The "' + cashflowObj.text + '" cashflow already exists.'
        );
      }
    },
    casfhlowsChanged() {
      this.unsavedChanges = true;
    },
    showModal() {
      this.$refs.manageCashflowsModal.showModal();
    },
    async save() {
      try {
        let body = [];

        for (let userCashflow of this.userCollCashflows) {
          body.push(userCashflow.value);
        }

        await service.saveUserCollateralCashflows(body);
        this.unsavedChanges = false;
        this.$refs.alert.showAlert('success', 'Saved casfhlows in the database.');
      } catch (err) {
        const errMessage =
          err.response.data.Message ||
          'Failed to save in the database. Please verify your cashflows.';
        await service.log(TRACE_ERROR, errMessage, err.stack);
        this.$refs.alert.showAlert('danger', errMessage);
      }
    }
  },
  async beforeRouteLeave(to, from, next) {
    let proceedRouting = true;
    if (this.unsavedChanges) {
      const shouldSave = window.confirm('You have unsaved changes! Do you want to save them?');
      if (shouldSave) {
        proceedRouting = await this.save();
      }
    }
    next(proceedRouting);
  },
  components: {
    UserCollCashflow,
    ManageCashflowsModal,
    Alert
  }
};
</script>
